// frontend/src/components/subscription/UpgradePrompt.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, Chip } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StarIcon from '@mui/icons-material/Star';

const FEATURE_INFO = {
  'unlimited_hints':          { title: 'Unlimited Hints',         desc: 'Get step-by-step help on every question — no limits.', icon: '💡' },
  'unlimited_bookmarks':      { title: 'Unlimited Saved Items',   desc: 'Save as many questions, examples, and posts as you need.', icon: '🔖' },
  'spaced_repetition':        { title: 'Smart Study',             desc: 'Spaced repetition brings back questions at the perfect time for long-term retention.', icon: '🧠' },
  'detailed_analytics':       { title: 'Detailed Analytics',      desc: 'See exactly where you\'re strong and where you need work — by topic, difficulty, and over time.', icon: '📊' },
  'study_plans':              { title: 'Study Plans',             desc: 'Auto-generated weekly plans based on your progress and upcoming exams.', icon: '📋' },
  'exam_countdown':           { title: 'Exam Countdown',          desc: 'Enter your exam date and get a custom revision schedule.', icon: '⏰' },
  'performance_benchmarking': { title: 'Performance Insights',    desc: 'See how you compare — "Top 20% in Algebra for Grade 10".', icon: '🏆' },
  'mistake_patterns':         { title: 'Mistake Analysis',        desc: 'We spot your recurring errors so you can fix them.', icon: '🔍' },
  'custom_sessions':          { title: 'Custom Sessions',         desc: 'Build your own practice sets by topic, difficulty, and question type.', icon: '🎯' },
  'certificates':             { title: 'Certificates',            desc: 'Earn shareable, downloadable certificates for your achievements.', icon: '🎓' },
  'parent_dashboard':         { title: 'Parent Dashboard',        desc: 'Track your child\'s progress with weekly reports and study insights.', icon: '👨‍👩‍👧' },
  'class_management':         { title: 'Class Management',        desc: 'Manage your entire class — assign topics, track progress, identify learners who need help.', icon: '🏫' }
};

const UpgradePrompt = ({ feature, compact = false }) => {
  const navigate = useNavigate();
  const info = FEATURE_INFO[feature] || { title: 'Premium Feature', desc: 'Upgrade to unlock this feature.', icon: '⭐' };

  if (compact) {
    return (
      <Box
        onClick={() => navigate('/pricing')}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
          bgcolor: 'action.hover', borderRadius: 2, cursor: 'pointer',
          transition: 'background 0.15s',
          '&:hover': { bgcolor: 'action.selected' }
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          Upgrade to Pro to unlock {info.title.toLowerCase()}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4, textAlign: 'center',
        bgcolor: 'background.default',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 3
      }}
    >
      <Typography variant="h2" sx={{ mb: 1.5 }}>{info.icon}</Typography>

      <Chip
        icon={<StarIcon sx={{ fontSize: 14 }} />}
        label="PRO"
        size="small"
        color="primary"
        variant="outlined"
        sx={{ mb: 1.5, fontWeight: 700 }}
      />

      <Typography variant="h6" fontWeight={600} gutterBottom>
        {info.title}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
        {info.desc}
      </Typography>

      <Button
        variant="contained"
        onClick={() => navigate('/pricing')}
        sx={{
          borderRadius: 20, px: 4,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 2
        }}
      >
        Upgrade to Pro — R59/month
      </Button>

      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.disabled' }}>
        Start with a 7-day free trial · Cancel anytime
      </Typography>
    </Paper>
  );
};

export default UpgradePrompt;
